import { FormSection } from 'framework/ui/layout';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { TextInput, TextArea, useFormContext, CurrencyInput, DateInput } from 'framework/form';
import { BillingScheduleItemResult } from '../billingService';
import { ARTIFACT_ID } from 'domain/shared/artifact';
import { ArtifactUploaderInput } from 'domain/shared/artifact/input';
import { SCHEDULE_ITEM_TYPE } from 'domain/projects/newProject/billing/constant';
import { formatPeriod, formatCustomPeriod, formatChangeRequestAlias } from '../billingUtils';
import { DATE_FORMAT } from 'framework/utils';
import { Button } from 'framework/ui/mui-builder';
import React, { useMemo } from 'react';
import { BillingType } from 'domain/shared/newProject';

const MAX_SIZE = 8388608;

const isPeriodicBillingType = (type: string): boolean =>
  type === BillingType.PERIODIC_PREPAID ||
  type === BillingType.PERIODIC_POSTPAID ||
  type === SCHEDULE_ITEM_TYPE.CALENDAR_PERIOD ||
  type === SCHEDULE_ITEM_TYPE.CUSTOM_PERIOD;

const calculateTotal = (items: BillingScheduleItemResult[]) => {
  const total = items.reduce((sum, item) => {
    const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
    return sum + (amount || 0);
  }, 0);
  const currency = items[0]?.currency || '';
  return { totalAmount: total, currency };
};

const getPeriodDisplayValue = (item: any): string => {
  if (item.changeRequestAlias) {
    return formatChangeRequestAlias(item.changeRequestAlias);
  }

  const isCustomPeriod = item.type === SCHEDULE_ITEM_TYPE.CUSTOM_PERIOD;

  if (isCustomPeriod) {
    return formatCustomPeriod(item.startDate, item.endDate);
  }

  return formatPeriod(item.position, item.periodMonth);
};

const getItemSource = (index: number, isPeriodic: boolean): string =>
  isPeriodic ? `data.${index}.periodMonth` : `data.${index}.milestoneName`;

const getItemValue = (item: any, isPeriodic: boolean): string =>
  isPeriodic ? getPeriodDisplayValue(item) : item.milestoneName;

interface MigrateBillingRequestFormProps {
  submitLabel?: string;
  isSubmitted?: boolean;
}

const MigrateBillingRequestForm = ({ submitLabel = 'Migrate', isSubmitted }: MigrateBillingRequestFormProps) => {
  const { data = {}, isSubmitEnabled, submitForm } = useFormContext();

  const scheduleItems = useMemo(() => {
    return (data.data as BillingScheduleItemResult[]) || [];
  }, [data.data]);

  const { totalAmount, currency } = useMemo(() => calculateTotal(scheduleItems), [scheduleItems]);

  const isPeriodic = isPeriodicBillingType(data.type || '');

  return (
    <FormSection>
      <Stack spacing={2} mb={4}>
        <Grid container spacing={2}>
          {scheduleItems.map((item: any, index) => {
            const isChangeRequest = item.type === 'CHANGE_REQUEST';
            const displayValue = getPeriodDisplayValue(item);
            const itemSource = getItemSource(index, isPeriodic);
            const itemValue = getItemValue(item, isPeriodic);

            return (
              <React.Fragment key={item.formItemId || `item-${index}`}>
                <Grid size={5}>
                  {isChangeRequest ? (
                    <TextInput
                      source={`data.${index}.changeRequestAlias`}
                      label="Change request"
                      disabled
                      value={displayValue}
                    />
                  ) : (
                    <TextInput
                      source={itemSource}
                      disabled
                      label={isPeriodic ? 'Period' : 'Billed milestone'}
                      value={itemValue}
                    />
                  )}
                </Grid>
                <Grid size={3}>
                  <CurrencyInput
                    label="Actual amount"
                    source={`data.${index}.amount`}
                    suffix={item.currency || ''}
                    isControlledValue
                    disabled={isSubmitted}
                  />
                </Grid>
                {isPeriodic && (
                  <Grid size={3}>
                    <TextInput source={`data.${index}.hours`} label="Actual hours" disabled={isSubmitted} />
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
          {scheduleItems.length > 1 && (
            <Grid size={8}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Total: {totalAmount.toLocaleString()} {currency}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid size={8}>
            <TextArea source="note" placeholder="Note" disabled={isSubmitted} />
          </Grid>
          <Grid size={8}>
            <DateInput
              source="billingRequestDate"
              label="Billing request date"
              dateFormat={DATE_FORMAT.API_ONLY_DATE}
              isRequired
              disabled={isSubmitted}
            />
          </Grid>
        </Grid>
        <ArtifactUploaderInput
          maxElements={10}
          maximumSize={MAX_SIZE}
          label="Billing request"
          source="artifactId"
          artifactTypeId={ARTIFACT_ID.ARTIFACT_STORAGE_FILE}
          disabled={isSubmitted}
        />
        {!isSubmitted && (
          <Grid container direction="row" spacing={1} sx={{ justifyContent: 'end', alignItems: 'center' }}>
            <Grid>
              <Button disabled={!isSubmitEnabled} onClick={() => submitForm?.()}>
                {submitLabel}
              </Button>
            </Grid>
          </Grid>
        )}
      </Stack>
    </FormSection>
  );
};

export default MigrateBillingRequestForm;
